import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { first, takeUntil, tap } from 'rxjs/operators';
import { AccountStoreService } from 'src/app/modules/account/store/account.store.service';
import { PrivacyModalService } from 'src/app/modules/privacy-policy/components/services/modal.service';
import { InstructorStoreService } from '../../client/store/instructor.store.service';
import { CourseTemplate } from '../../models/course-template';
import { Instructor } from '../../models/instructor';
import { Multimedia } from '@shared/models/multimedia';
import { TRANSLATION_SCOPE } from '@modules/instructor/i18n/scope';
import { Router, UrlTree } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { FileViewerDialogService } from '@modules/core/services/file-viewer-dialog.service';
import { NotificationService } from "@core/services/notification.service";
import { InvalidFileItem } from "angular-file/file-upload/fileTools";
import { InstructorApiService } from "@modules/instructor/client/services/instructor.api.service";
import { instructorFeatureKey as instructors } from '@modules/instructor/client/store/instructor.state';
import { AlertMessageType } from '@modules/shared/enums/alert-message-type';

@Component({
  // tslint:disable-next-line: component-selector
  selector: 'my-courses-create',
  templateUrl: './my-courses.component.html',
  styleUrls: ['./my-courses.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ExperienceComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public isTabReadOnly: boolean = false;

  @Output() OnBack = new EventEmitter<string>();

  @Output() OnSubmit = new EventEmitter<string>();

  @Output() OnComplete = new EventEmitter<string>();

  haveAccess = [
    {
      label: 'Ja',
      value: true,
    },
    {
      label: 'Nei',
      value: false,
    },
  ];

  instructor$: Observable<Instructor>;
  _instructor: Instructor;
  courseTemplates$: Observable<CourseTemplate[]>;

  instructorCv: File;
  instructorCertificate: File;

  instructorForm: FormGroup;
  notificationPreferenceFormControl: FormControl;
  isUpdate = false;

  isIdDocumentUpload = false;
  isAccountVerified = false;
  translationScope = TRANSLATION_SCOPE;
  // Private
  private destroy$: Subject<any>;
  invalidFile: InvalidFileItem[] = null;
  hasPreference: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId,
    private instructorStore: InstructorStoreService,
    private accountStore: AccountStoreService,
    private privacyModalService: PrivacyModalService,
    private router: Router,
    private fileViewerDialogService: FileViewerDialogService,
    private notificationService: NotificationService,
    private instructorApiService: InstructorApiService
  ) {
    this.initializeInstructorForm();
    this.createNotificationPreferenceForm();
    this.destroy$ = new Subject();
  }

  get isBrowser(): boolean {
    if(isPlatformBrowser(this.platformId)) return true;
    return false;
  }

  ngOnInit() {
    this.courseTemplates$ = this.instructorStore.getCourseTemplate();
    this.instructor$ = this.instructorStore.getInstructorProfile().pipe(
      takeUntil(this.destroy$),
      tap((instructor) => {
        this._instructor = instructor;
        this.isUpdate = true;

        if(!this.hasPreference)
        {
          this.hasPreference = true;
          this.instructorApiService.getNotificationPreference(this._instructor.id)
            .pipe(
              takeUntil(this.destroy$),
              first()
            )
            .subscribe((preference) => {
              this.patchNotificationPreferenceForm(preference);
            });
        }

        if (!this.instructorForm.dirty) {
          this.patchFormData(instructor);
        }
      })
    );

    // Check Bank Account Status
    this.accountStore
      .getBusinessAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe((account) => {
        if (account) {
          if (account.status === 'VERIFIED') {
            this.isAccountVerified = true;
          } else {
            setTimeout(() => {
              this.accountStore.fetchCurrentAccount();
            }, 8000);
          }
        }
      });

    // Check ID Document Status
    this.accountStore
      .getKycDocument()
      .pipe(takeUntil(this.destroy$))
      .subscribe((documents) => {
        if (documents.length) {
          const isPending = documents.find((doc) => doc.status === 'PENDING');
          if (!isPending) {
            this.isIdDocumentUpload = true;
          }
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.isTabReadOnly.currentValue) {
      this.instructorForm.disable();
      this.notificationPreferenceFormControl.disable();
    } else {
      this.instructorForm.enable();
      this.notificationPreferenceFormControl.enable();
    }
  }

  ngOnDestroy(): void {
    this.formSubmit(true);
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelectTemplate(): void {
    const selectedTemplates: string[] = this.instructorForm.get('courseTemplates').value;
    if (selectedTemplates.length) {
      const templates = selectedTemplates.map((courseTemplateId) => {
        return { courseTemplateId };
      });

      this.instructorStore.updateCourseTemplate(this._instructor.id, templates);
    }
  }

  onRemoveTemplate(): void {
    const selectedTemplates: string[] = this.instructorForm.get('courseTemplates').value;
    const templates = selectedTemplates.map((courseTemplateId) => {
      return { courseTemplateId };
    });
    this.instructorStore.updateCourseTemplate(this._instructor.id, templates);
  }

  enableFormSubmit(): boolean {
    if (this.instructorForm.valid && this.isCertificateUpload && this.isIdDocumentUpload && this.isAccountVerified) {
      return false;
    }
    return true;
  }

  public formSubmit(silentUpdate?: boolean): void {
    if (this.instructorForm.invalid) {
      this.instructorForm.markAsTouched();
      return;
    }

    const instructor: Instructor = {
      id: this._instructor.id,
      userId: this._instructor.userId,
      firstName: this._instructor.firstName,
      lastName: this._instructor.lastName,
      name: this._instructor.name,
      email: this._instructor.email,
      address: this._instructor.address,
      phone: this._instructor.phone,
      birthDate: this._instructor.birthDate,
      organizationNo: this._instructor.organizationNo,
      organizationName: this._instructor.organizationName,
      description: this._instructor.description,
      isDraft: this._instructor.isDraft,
      regions: this._instructor.regions,
      ...this.instructorForm.value,
    };
    delete instructor.courseTemplates;

    if (!silentUpdate) {
      this.instructorStore.completeInstructor(this._instructor.id, instructor);
    } else {
      if (this.instructorForm.dirty) {
        this.instructorStore.updateInstructor(this._instructor.id, instructor, silentUpdate);
      }
    }
    if(this.isBrowser) {
      window.scroll({
        top: 70,
        left: 100,
        behavior: 'smooth',
      });
    }
  }

  public backToBankInfo(): void {
    if(this.isBrowser) {
      window.scroll({
        top: 70,
        left: 100,
        behavior: 'smooth',
      });
    }
    this.OnBack.emit('bank-account');
  }

  onSelectHavBoatAccess(value: boolean): void {
    if (value) {
      this.instructorForm.get('boat.type').setValidators(Validators.required);
      this.instructorForm.get('boat.type').updateValueAndValidity();
    } else {
      this.instructorForm.get('boat.type').clearValidators();
      this.instructorForm.get('boat.type').setErrors(null);
    }
  }

  uploadCv(file: File): void {
    setTimeout(() => {
      this.instructorStore.uploadMedia(this._instructor.id, file, 'instructor cv', 'cv', false);
    }, 100);
  }

  uploadCertificate(file: File): void {
    setTimeout(() => {
      this.instructorStore.uploadMedia(this._instructor.id, file, 'instructor certificate', 'certificate', false);
    }, 100);
  }

  removeCvOrCertificate(name: string): void {
    this.instructorStore.removeInstructorMedia(this._instructor.id, name);
  }

  openFilePreviwer(file: Multimedia) {
    this.fileViewerDialogService.openFilePreviewer(file);
  }

  checkFileContentType(file: Multimedia): string {
    return this.fileViewerDialogService.assignContentType(file.contentType);
  }

  openTermsConditionPage(): void {
    this.privacyModalService.openInstructorTermsAndCondition();
  }

  getCvOrCertificate(medias: Multimedia[]): Multimedia[] {
    return medias.filter((media) => media.category === 'cv' || media.category === 'certificate');
  }

  get isCertificateUpload(): boolean {
    if (this._instructor) {
      const findCv = this._findMedia('cv');
      //const findCertificate = this._findMedia('certificate');
      if (findCv) {
        return true;
      }
    }
    return false;
  }

  initializeInstructorForm(): void {
    this.instructorForm = new FormGroup({
      courseTemplates: new FormControl('', [Validators.required]),
      haveBoat: new FormControl(null, [Validators.required]),
      boat: new FormGroup({
        type: new FormControl(''),
      }),
      hasClassroomAccess: new FormControl(null, [Validators.required]),
      havePc: new FormControl('', [Validators.required]),
      acceptTnC: new FormControl('', [Validators.required]),
    });
  }

  patchFormData(instructor: Instructor): void {
    this.instructorForm.patchValue({
      courseTemplates: instructor.courseTemplates.map((temp) => temp.id),
      haveBoat: instructor.haveBoat,
      boat: instructor.boat,
      hasClassroomAccess: instructor.hasClassroomAccess,
      havePc: instructor.havePc,
      acceptTnC: instructor.acceptTnC,
    });
  }

  isInvalidateControl(controlName: string, validationType: string): boolean {
    return (
      this.instructorForm.get(controlName).hasError(validationType) &&
      (this.instructorForm.get(controlName).dirty || this.instructorForm.get(controlName).touched)
    );
  }

  openInstructorTemplatePage() {
    const instructorPageUrlTree: UrlTree = this.router.createUrlTree([`/kursoversikt-for-tilbydere`]);
    const serializedUrl = this.router.serializeUrl(instructorPageUrlTree);
    if(this.isBrowser) window.open(serializedUrl,'_blank');
  }

  _findMedia(category: string): Multimedia {
    return this._instructor.medias.find((media) => media.category === category);
  }

  notifyInvalidType() {
    for(const file of this.invalidFile)
    {
      if(file.type === 'accept')
      {
        this.notificationService.error('Please select a valid file format to upload your CV/certificate');
      }
      else if(file.type === 'fileSize')
      {
        this.notificationService.error('Maximum file size is 6MB');
      }
    }

    this.invalidFile = null;
  }

  setNotificationPreference() {
    this.instructorApiService.setNotificationPreference(this._instructor.id, this.notificationPreferenceFormControl.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (preference) => {
        return this.notificationService.success('notification_of_new_course_requests_has_been_updated', instructors, AlertMessageType.SAVED);

        },
        (error) => {
          this.notificationPreferenceFormControl.setValue(!this.notificationPreferenceFormControl.value);
          return this.notificationService.error('Failed to update notification preference');
        });
  }

  private createNotificationPreferenceForm(): void {
    this.notificationPreferenceFormControl = new FormControl('true');
  }

  private patchNotificationPreferenceForm(preference: boolean): void {
    this.notificationPreferenceFormControl.patchValue(preference);
  }
}
